/**
 * SA-1092 §3.7: the Triage tab's rows — one per waiting item. The title
 * (subject or group name), the sender beneath, the kind and flag chips, the
 * wait, unread bold. A group item is selected by its message; clicking one
 * opens the thread at it (the page's `onSelect`).
 */
import { Badge, Button, Checkbox, Spinner } from '@/components/ui'
import { cn } from '@/utils'
import { usePermission } from '@/hooks/usePermission'
import type { QueueItem } from '@/types/inbox'
import { channelIcon } from './channels'
import { flagChips, kindChip } from './triageChips'
import { QUEUE_EMPTY, QUEUE_LOAD_MORE, QUEUE_NEW, waitText } from './queueText'
import { tourAnchor } from '@/components/tour/tourAnchor'
import { selectLabel } from './triageText'
import { TriageSelectionBar } from './TriageSelectionBar'
import { useQueueBulk } from './useQueueBulk'

interface QueueListProps {
  items: QueueItem[]
  total: number
  selected: { conversationId: string | null; messageId: string | null }
  isLoading: boolean
  error: string | null
  arrivedIds: string[]
  onSelect: (item: QueueItem) => void
  onLoadMore: () => void
  onClearArrivals?: () => void
  hasMore: boolean
  now?: Date
  /** The deployment's record noun, lower case. */
  noun: string
  /** Changes with the filters: a new value clears the selection. */
  resetToken: string
}

function isSelected(item: QueueItem, selected: QueueListProps['selected']): boolean {
  if (item.conversationId !== selected.conversationId) return false
  return item.messageId === null || item.messageId === selected.messageId
}

export function QueueList({ items, total, selected, isLoading, error, arrivedIds, onSelect, onLoadMore, onClearArrivals, hasMore, now = new Date(), noun, resetToken }: QueueListProps) {
  // Selection is offered only to a viewer who could decide the message on its own (spec anti-goal).
  const canTriage = usePermission('inbox:triage')
  const bulk = useQueueBulk({ items, noun, resetToken })
  const bar = canTriage && bulk.barOn ? (
    <div className="sticky top-0 z-10 border-b border-border"><TriageSelectionBar {...bulk.barProps} /></div>
  ) : null
  if (isLoading && items.length === 0) {
    return <div className="flex items-center justify-center h-32"><Spinner /></div>
  }
  if (items.length === 0) {
    return (
      <div>
        {bar}
        <p className="px-4 py-8 text-center text-sm text-muted-foreground">{QUEUE_EMPTY}</p>
      </div>
    )
  }
  return (
    <div {...tourAnchor('inbox.list.rows')} className="relative h-full overflow-y-auto">
      {bar}
      {arrivedIds.length > 0 && (
        <div className="sticky top-2 z-10 flex justify-center">
          <Button size="sm" variant="default" className="h-7 text-xs shadow" onClick={() => {
            document.getElementById(`queue-${arrivedIds[0]}`)?.scrollIntoView({ block: 'nearest' })
            onClearArrivals?.()
          }}>{QUEUE_NEW(arrivedIds.length)}</Button>
        </div>
      )}
      {error && <p className="px-4 py-2 text-xs text-destructive">{error}</p>}
      {items.map((item) => {
        const Icon = channelIcon(item.channel)
        // SA-1294: a chip per kind waiting on the item; an older server sends only `kind`.
        const kinds = (item.kinds.length ? item.kinds : [{ kind: item.kind, kindLabel: item.kindLabel }])
          .map((k) => kindChip(k.kind, k.kindLabel))
          .filter((chip): chip is NonNullable<typeof chip> => chip !== null)
        const chips = [...kinds, ...flagChips([item.flag])]
        const current = isSelected(item, selected)
        const title = item.title ?? item.senderName ?? 'Message'
        return (
          <div
            key={item.id}
            id={`queue-${item.id}`}
            // SA-1123: the sidebar's active row — a neutral wash and a primary bar; every row carries the bar's width.
            className={cn('flex items-start gap-1 pl-3 border-b border-border border-l-2 transition-colors', current ? 'bg-accent border-l-primary' : 'border-l-transparent hover:bg-accent')}
          >
            {canTriage && (
              <Checkbox
                className="mt-4"
                checked={bulk.sel.isSelected(item.id)}
                aria-label={selectLabel(title)}
                disabled={bulk.running}
                onCheckedChange={() => {}}
                onClick={(e) => { e.preventDefault(); bulk.sel.toggle(item.id, e.shiftKey) }}
              />
            )}
            <button
              type="button"
              aria-current={current ? 'true' : 'false'}
              aria-label={`${title}, waiting ${waitText(item.waitingSince, now)}`}
              onClick={() => onSelect(item)}
              className="min-w-0 flex-1 flex items-start gap-3 pl-2 pr-4 py-3 text-left"
            >
              <Icon className="mt-0.5 h-5 w-5 shrink-0 text-muted-foreground" />
              <div className="min-w-0 flex-1">
                <div className="flex items-center justify-between gap-2">
                  <span className={cn('truncate text-sm', item.unread > 0 ? 'font-semibold text-foreground' : 'text-foreground')}>{title}</span>
                  <span className="shrink-0 text-xs text-muted-foreground">{waitText(item.waitingSince, now)}</span>
                </div>
                {item.senderName && item.title && <p className="truncate text-xs text-muted-foreground">{item.senderName}</p>}
                {item.preview && <p className="truncate text-xs text-muted-foreground">{item.preview}</p>}
                {chips.length > 0 && (
                  <div className="mt-1 flex flex-wrap gap-1">
                    {chips.map((chip) => <Badge key={chip.key} variant={chip.variant} className="px-1.5 py-0 text-[10px]">{chip.label}</Badge>)}
                  </div>
                )}
              </div>
            </button>
          </div>
        )
      })}
      {hasMore && (
        <div className="p-3 text-center">
          <Button size="sm" variant="secondary" className="text-xs" onClick={onLoadMore}>{QUEUE_LOAD_MORE} ({items.length} of {total})</Button>
        </div>
      )}
    </div>
  )
}
